import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit,
  SimpleChanges, ViewChild
} from '@angular/core';
import { Point } from 'geojson';
import { MapElement } from '../../map-element.model';
import { StateService } from '../../../state/state.service';
import { Subject } from 'rxjs';
import { filter, takeUntil, map, skipUntil, startWith, debounceTime } from 'rxjs/operators';
import { getCSSColorVariableValue } from '../../../utils';
import { FlagService } from '../../../flag/flag.service';
import { AlarmService } from '../../../../alarm/alarm.service';
import { LinkingCrudService } from '../../../../linking/linking.service';
import { MapService, MapMarker } from '../../map.service';
import { environment } from '../../../../../environments/environment';
import { InfoActionService } from '../../../../element-utils/element-actions/info-action/info-action.service';

/**
 * This component is used to display html markers on top of Mapbox GL map.
 * Marker description: https://www.mapbox.com/mapbox-gl-js/api/#marker
 * Inspiration: https://github.com/Wykks/ngx-mapbox-gl/blob/master/src/app/lib/marker/marker.component.ts
 */
@Component({
  selector: 'qm-marker',
  templateUrl: './marker.component.html',
  styleUrls: ['./marker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarkerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  /**
   * Current marker html element
   */
  @ViewChild('content', { static: true }) content: ElementRef;

  /**
   * Current GeoJSON Point
   */
  @Input() public feature: MapElement<Point>;

  public attrId: string;

  /**
   * Used map library's marker instance, which implements required methods
   */
  markerInstance: MapMarker;

  public isFlagged: boolean = false;

  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<boolean> = new Subject();
  private oldZoom: number;

  /**
   * Marker's selection
   */
  public isSelected: boolean = false;

  public isLabelVisible$ = this.mapService.zoom$
    .pipe(
      takeUntil(this.ngUnsubscribe),
      map(zoom => !!this.feature?.layer?.style?.label?.visibleFrom
                    && zoom > this.feature.layer.style.label.visibleFrom)
    );

  public markerFillColor: string;

  public markerRotation: number;

  public markerHasAlarms: boolean = false;

  /**
   * Default constructor
   * @param {NgZone} zone
   * @param mapService
   * @param stateService
   */
  constructor(
    private zone: NgZone,
    private mapService: MapService,
    private flagService: FlagService,
    private stateService: StateService,
    private alarmService: AlarmService,
    private readonly linkingService: LinkingCrudService,
    private changeDetector: ChangeDetectorRef,
    private infoActionService: InfoActionService) { }

  ngOnInit() {

    this.attrId = this.getId();
    this.markerFillColor = this.getFillColor();

    if (this.markerInstance) {
      this.zone.runOutsideAngular(() => {
        this.markerInstance.remove();
        this.markerInstance = null;
        this.markerInstance = this.mapService.createNewMarker({
          lng: this.feature.geometry.coordinates[0],
          lat: this.feature.geometry.coordinates[1]
        }, this.content.nativeElement);
      });
    }

    this.flagService.flags$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((flags) => {
        const foundFlag = flags.find((f: any) => {
          return f.targetObjectId === this.feature.id.toString();
        });
        this.isFlagged = foundFlag !== undefined;
        this.changeDetector.detectChanges();
      });
  }

  /**
   * Angular input change detection hook. If feature is changed, update marker position.
   * @param {SimpleChanges} changes
   */
  ngOnChanges(changes: SimpleChanges) {
    let coordinatesChanged: boolean;
    if (!changes.feature.isFirstChange()) {
      const prevCoords = changes.feature.previousValue.geometry.coordinates;
      const curCoords = changes.feature.currentValue.geometry.coordinates;
      coordinatesChanged = prevCoords[0] !== curCoords[0] || prevCoords[1] !== curCoords[1];
    } else {
      coordinatesChanged = true;
    }

    if (coordinatesChanged && this.markerInstance) {
      this.zone.runOutsideAngular(() => {
        this.markerInstance.remove();
        this.markerInstance = null;
        this.markerInstance = this.mapService.createNewMarker({
          lng: this.feature.geometry.coordinates[0],
          lat: this.feature.geometry.coordinates[1]
        }, this.content.nativeElement);
      });
    }

    this.attrId = this.getId();
    this.markerFillColor = this.getFillColor();
    this.markerRotation = this.getReverseRotation(this.feature.properties.direction);
    this.changeDetector.markForCheck();
  }

  /**
   * Angular lifecycle hook, add marker to map.
   */
  ngAfterViewInit() {
    this.alarmService.alarmsForFeature$(this.feature).pipe(
      takeUntil(this.ngUnsubscribe),
    ).subscribe(alarms => {
      this.markerHasAlarms = alarms.length > 0;
      this.markerFillColor = this.getFillColor();
      this.changeDetector.markForCheck();
    });

    if (this.content) {
      this.zone.runOutsideAngular(() => {
        this.markerInstance = this.mapService.createNewMarker({
          lng: this.feature.geometry.coordinates[0],
          lat: this.feature.geometry.coordinates[1]
        }, this.content.nativeElement);
      });
    }

   /**
    * Toggle marker's selection according to map service
    */
    this.mapService.currentFeature$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        map(currentFeature => !!currentFeature && currentFeature.id === this.feature.id),
        filter(isThisMarkerSelected => this.isSelected !== isThisMarkerSelected) // Continue if value changes
      )
      .subscribe(isThisMarkerSelected => {
        this.isSelected = isThisMarkerSelected;
        this.changeDetector.detectChanges();
      });
  }

  /**
   * Angular destructor event, remove marker from map.
   */
  ngOnDestroy() {
    if (this.markerInstance) {
      this.markerInstance.remove();
      this.markerInstance = null;
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getId() {
    return 'marker_' + this.feature.layer.id + '_' + this.feature.properties[this.feature.layer.metadata.uniqueBy];
  }

  public withOutSubTitle(a: string) {
    const test = /\r?\n|\r/;
    const test2 = /\d[0-9]*/;
    if (test.test(a)) {
      return a.split('\n');
    } else if (test2.test(a)) {
      return [a];
    }
  }

  public getReverseRotation(rotation: number) {
    if (rotation >= 0) {
      return 0 - rotation;
    } else {
      return Math.abs(rotation);
    }
  }

  private getFillColor(): string {
    let color = '';
    if (this.markerHasAlarms) {
      color = getCSSColorVariableValue('danger-base');
    } else {
      try {
        color = this.feature.layer.style.icon.fill.toString();
      } catch (e) {
        color = getCSSColorVariableValue('success-base');
      }
    }
    return color;
  }

  /**
   * Click handler, select current feature
   */
  public onClick($event: Event) {
    console.log('MARKER CLICK', this.feature);
    console.log($event);
    $event.preventDefault();
    $event.stopPropagation();
    $event['originalEvent'] = $event;
    console.log({ data: this.feature, event: { elementId: this.getId() } });
    this.mapService.changeHoverFeature({ data: this.feature, event: { elementId: this.getId() } });
  }

  /**
   * Double click handler, open element info
   */
  public onDBLClick($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();
    if (!!this.feature.layer.metadata.component) {
      this.mapService.changeCurrentFeature(this.feature);
      this.infoActionService.openDetails(this.feature.layer, this.feature);
      this.mapService.changeHoverFeature(null);
    }
  }
}
